import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Phone, Menu, X, ArrowUpRight } from "lucide-react";
import Logo from "@/components/Logo";
import { SITE } from "@/lib/api";

const LINKS = [
  { to: "/", label: "Home", testId: "nav-home" },
  { to: "/chi-siamo", label: "Chi Siamo", testId: "nav-chi-siamo" },
  { to: "/corsi", label: "I Corsi", testId: "nav-corsi" },
  { to: "/blog", label: "Blog", testId: "nav-blog" },
  { to: "/contatti", label: "Contatti", testId: "nav-contatti" },
];

export default function NavigationHeader() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [pathname]);

  if (pathname.startsWith("/admin")) return null;

  const hasDarkHero = pathname === "/";
  const onDark = hasDarkHero && !scrolled && !open;

  return (
    <header
      className={`fixed inset-x-0 top-0 z-50 transition-[background-color,border-color,box-shadow] duration-500 ${
        scrolled || open
          ? "border-b border-[#D8E5E8] bg-white/90 shadow-[0_12px_40px_-24px_rgba(16,51,59,0.35)] backdrop-blur-xl"
          : "border-b border-transparent bg-transparent"
      }`}
    >
      <div className="mx-auto flex h-[76px] max-w-7xl items-center justify-between px-5 sm:px-8">
        <Link to="/" data-testid="nav-logo" aria-label="Scuola Didattica Sicura — Home">
          <Logo dark={onDark} />
        </Link>

        <nav className="hidden items-center gap-1 lg:flex" aria-label="Navigazione principale">
          {LINKS.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={l.testId}
              className={({ isActive }) =>
                `rounded-full px-4 py-2 text-sm font-medium transition-colors duration-300 ${
                  isActive
                    ? onDark
                      ? "bg-white/15 text-white"
                      : "bg-[#E8F2F4] text-[#1F8296]"
                    : onDark
                      ? "text-[#DCECEF] hover:bg-white/10 hover:text-white"
                      : "text-[#42565D] hover:bg-[#F2F7F8] hover:text-[#1F8296]"
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
        </nav>

        <div className="hidden items-center gap-3 lg:flex">
          <a
            href={`tel:${SITE.phone}`}
            data-testid="nav-phone"
            className={`group inline-flex items-center gap-2 rounded-full border px-4 py-2 text-sm font-semibold transition-colors duration-300 ${
              onDark
                ? "border-white/40 text-white hover:border-[#7AD9E8] hover:text-[#7AD9E8]"
                : "border-[#C9DBDF] text-[#33484F] hover:border-[#1F8296]/60 hover:text-[#1F8296]"
            }`}
          >
            <Phone className="h-4 w-4 transition-transform duration-300 group-hover:rotate-12" />
            {SITE.phoneDisplay}
          </a>
          <Link
            to="/contatti"
            data-testid="nav-cta"
            className="group inline-flex items-center gap-1.5 rounded-full bg-[#2997AB] px-5 py-2.5 text-sm font-bold text-white transition-colors duration-300 hover:bg-[#238496]"
          >
            Richiedi informazioni
            <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </Link>
        </div>

        <button
          type="button"
          data-testid="mobile-menu-button"
          onClick={() => setOpen((v) => !v)}
          className={`inline-flex h-11 w-11 items-center justify-center rounded-full border transition-colors lg:hidden ${
            onDark ? "border-white/40 text-white" : "border-[#C9DBDF] text-[#33484F]"
          }`}
          aria-label={open ? "Chiudi menu" : "Apri menu"}
        >
          {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            className="overflow-hidden border-b border-[#D8E5E8] bg-white/95 backdrop-blur-xl lg:hidden"
            aria-label="Navigazione mobile"
          >
            <div className="space-y-1 px-5 py-5">
              {LINKS.map((l) => (
                <NavLink
                  key={l.to}
                  to={l.to}
                  data-testid={`mobile-${l.testId}`}
                  className={({ isActive }) =>
                    `block rounded-xl px-4 py-3 text-base font-medium ${
                      isActive ? "bg-[#E8F2F4] text-[#1F8296]" : "text-[#33484F]"
                    }`
                  }
                >
                  {l.label}
                </NavLink>
              ))}
              <a
                href={`tel:${SITE.phone}`}
                data-testid="mobile-nav-phone"
                className="mt-2 flex items-center justify-center gap-2 rounded-xl bg-[#2997AB] px-4 py-3.5 text-base font-bold text-white"
              >
                <Phone className="h-4 w-4" /> {SITE.phoneDisplay}
              </a>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
}
